import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from 'store';
import brandbookService from '../services/brandbookApi';

interface Brandbook {
  id: number;
  user_id: string;
  display_name: string;
  slogan: string;
  logo_url: string;
  created_at: string;
  updated_at: string;
  user: null;
}

const BrandbookCard = ({ brandbook }: { brandbook: Brandbook }) => (
  <Link
    to={`/brandbook/view/${brandbook.id}`}
    className="block w-full transition-transform duration-200 hover:-translate-y-1"
  >
    <div className="w-full bg-background text-text-primary p-2 shadow-sm hover:shadow-md rounded-lg overflow-hidden transition-shadow duration-200">
      {brandbook.logo_url ? (
        <img
          src={brandbook.logo_url}
          className="w-full h-48 object-cover"
          alt={brandbook.slogan}
        />
      ) : (
        <div className="w-full h-48 bg-background-card flex items-center justify-center">
          <p className="">No logo available</p>
        </div>
      )}
      <div className="p-4">
        <h3 className="font-medium text-lg line-clamp-1 ">
          {brandbook.display_name}
        </h3>
        <p className="text-sm mt-2">
          Created: {new Date(brandbook.created_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  </Link>
);

export default function BrandBooksReference() {
  const { user } = useSelector(
    (state: RootState) => state.auth
  );
  const [isBrandbooksGenerated, setIsBrandbooksGenerated] = useState(false);
  const [brandbooks, setBrandbooks] = useState<Brandbook[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBrandbooks = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let response;
        if (user?.userId) {
          try {
            response = await brandbookService.getBrandbooksByUserId(
              user.userId,
            );

            if(response.success) {
              setIsBrandbooksGenerated(true);
            }

            // If user-specific request fails with 404, fetch all brandbooks
            if (
              !response.success &&
              response.message === "404: Not Found Brandbook's"
            ) {
              setIsBrandbooksGenerated(false);
              response = await brandbookService.getAllBrandbooks();
            }
          } catch (userError) {
            // If user-specific request fails for any reason, fetch all brandbooks
            console.log(userError);
            response = await brandbookService.getAllBrandbooks();
          }
        } else {
          response = await brandbookService.getAllBrandbooks();
        }

        if (response.success) {
          // @ts-expect-error response is not null
          setBrandbooks(response?.data);
        } else {
          setError('Failed to fetch brandbooks');
        }
      } catch (err) {
        setError('An error occurred while fetching brandbooks');
        console.error('Error fetching brandbooks:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrandbooks();
  }, [setBrandbooks, user?.userId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <p>Loading brandbooks...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center p-8 text-red-600">
        <p>{error}</p>
      </div>
    );
  }

  if (brandbooks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-text-primary bg-background-main rounded-lg">
        <h2 className="text-xl font-medium ">No Brandbooks Found</h2>
        <p className="mt-2 text-gray-600">
          {isBrandbooksGenerated ? (
            <>
              Start by{' '}
              <Link
                to="/brandbook/get-started"
                className="text-primary font-medium"
              >
                Creating your first brandbook
              </Link>
              !
            </>
          ) : (
            'Check back later for inspiration!'
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1260px] mx-auto">
      <div className="flex justify-between text-text-primary items-center mb-6">
        <h2 className="text-2xl font-medium ">
          {isBrandbooksGenerated
            ? 'Your Brandbooks'
            : 'Explore our Brandbook collection'}
        </h2>
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {brandbooks.slice(0, 9).map((brandbook) => (
          <li key={brandbook.id}>
            <BrandbookCard brandbook={brandbook} />
          </li>
        ))}
      </ul>
    </div>
  );
}
